import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import { putBulkEditEvents } from '../../api/user/events/Events'
import { EVENT_TYPE_LABELS_FILTERED, getEventLabel } from '../../types/EventTypes'
import CustomTabsMultiple from '../input/CustomTabsMultiple'
import handleToast from '../toast/handleToast'
import SelectionEditModal from './SelectionEditModal'
import SliderEditModal from './SliderEditModal'
import TextEditModal from './TextEditModal'
import TextareaEditModal from './TextareaEditModal'
import TimeEditModal from './TimeEditModal'

const SURFACE_TYPES = ['Hard', 'Clay', 'Indoor', 'Sand', 'Grass', 'Other']
const SKILL_LEVEL_LABELS = ['Beginner', 'Intermediate', 'Intermediate High', 'Advanced', 'Advanced 4.5+', 'Mixed-Level']

type BulkEditFormData = {
  title: string
  description: string
  type: string
  secondary_type: string
  skill_level: number[]
  surface: number | null
  court: string
  start_time: string
  duration: string
  fee: string
  venue_fixed_rate: string
  venue_hourly_rate: string
  max_players: number
  private_code: string
}

const INITIAL_FORM_DATA: BulkEditFormData = {
  title: '',
  description: '',
  type: '',
  secondary_type: '',
  skill_level: [],
  surface: null,
  court: '',
  start_time: '',
  duration: '',
  fee: '',
  venue_fixed_rate: '',
  venue_hourly_rate: '',
  max_players: 0,
  private_code: '',
}

const BulkEditRow = ({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) => (
  <Flex m="18px 0" alignItems="center" gap="12px">
    <Text variant="title" fontSize="lg" minW="180px">
      {label}
    </Text>
    <Text variant="subtle" fontSize="md" flex="1">
      {value}
    </Text>
    <Spacer />
    <Button variant="link" onClick={onEdit}>
      Edit
    </Button>
  </Flex>
)

const BulkEditEventsModal = ({
  isOpen,
  onClose,
  eventIds,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  eventIds: string[]
  onSuccess: () => void
}) => {
  const toast = useToast()
  const [formData, setFormData] = useState<BulkEditFormData>(INITIAL_FORM_DATA)
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({})
  const [selectedSkillLevels, setSelectedSkillLevels] = useState<number[]>([])
  const [conflictData, setConflictData] = useState<{ eligible_ids: string[]; blocked_events: { id: string; title: string; reason: string }[] } | null>(null)

  const { isOpen: isTitleOpen, onOpen: onTitleOpen, onClose: onTitleClose } = useDisclosure()
  const { isOpen: isDescriptionOpen, onOpen: onDescriptionOpen, onClose: onDescriptionClose } = useDisclosure()
  const { isOpen: isTypeOpen, onOpen: onTypeOpen, onClose: onTypeClose } = useDisclosure()
  const { isOpen: isSecondaryTypeOpen, onOpen: onSecondaryTypeOpen, onClose: onSecondaryTypeClose } = useDisclosure()
  const { isOpen: isSurfaceOpen, onOpen: onSurfaceOpen, onClose: onSurfaceClose } = useDisclosure()
  const { isOpen: isCourtOpen, onOpen: onCourtOpen, onClose: onCourtClose } = useDisclosure()
  const { isOpen: isTimeOpen, onOpen: onTimeOpen, onClose: onTimeClose } = useDisclosure()
  const { isOpen: isDurationOpen, onOpen: onDurationOpen, onClose: onDurationClose } = useDisclosure()
  const { isOpen: isFeeOpen, onOpen: onFeeOpen, onClose: onFeeClose } = useDisclosure()
  const { isOpen: isVenueFixedRateOpen, onOpen: onVenueFixedRateOpen, onClose: onVenueFixedRateClose } = useDisclosure()
  const { isOpen: isVenueHourlyRateOpen, onOpen: onVenueHourlyRateOpen, onClose: onVenueHourlyRateClose } = useDisclosure()
  const { isOpen: isMaxPlayersOpen, onOpen: onMaxPlayersOpen, onClose: onMaxPlayersClose } = useDisclosure()
  const { isOpen: isPrivateCodeOpen, onOpen: onPrivateCodeOpen, onClose: onPrivateCodeClose } = useDisclosure()
  const { isOpen: isSkillLevelOpen, onOpen: onSkillLevelOpen, onClose: onSkillLevelClose } = useDisclosure()

  useEffect(() => {
    if (!isOpen) {
      setConflictData(null)
      return
    }
    setFormData(INITIAL_FORM_DATA)
    setTouchedFields({})
    setSelectedSkillLevels([])
    setConflictData(null)
  }, [isOpen])

  const updateField = (field: keyof BulkEditFormData, value: string | number | number[] | null) => {
    setFormData((prev) => ({ ...prev, [field]: value as never }))
    setTouchedFields((prev) => ({ ...prev, [field]: true }))
    onTitleClose()
    onDescriptionClose()
    onTypeClose()
    onSecondaryTypeClose()
    onSurfaceClose()
    onCourtClose()
    onTimeClose()
    onDurationClose()
    onFeeClose()
    onVenueFixedRateClose()
    onVenueHourlyRateClose()
    onMaxPlayersClose()
    onPrivateCodeClose()
    onSkillLevelClose()
  }

  const payload = useMemo(() => {
    const data: Record<string, any> = {}
    if (touchedFields.title) data.title = formData.title
    if (touchedFields.description) data.description = formData.description
    if (touchedFields.type) data.type = formData.type
    if (touchedFields.secondary_type) data.secondary_type = formData.secondary_type
    if (touchedFields.skill_level) data.skill_level = formData.skill_level
    if (touchedFields.surface) data.surface = formData.surface
    if (touchedFields.court) data.court = formData.court
    if (touchedFields.start_time) data.start_time = formData.start_time
    if (touchedFields.duration) data.duration = parseInt(formData.duration, 10)
    if (touchedFields.fee) data.fee = formData.fee
    if (touchedFields.venue_fixed_rate) data.venue_fixed_rate = formData.venue_fixed_rate
    if (touchedFields.venue_hourly_rate) data.venue_hourly_rate = formData.venue_hourly_rate
    if (touchedFields.max_players) data.max_players = formData.max_players
    if (touchedFields.private_code) data.private_code = formData.private_code === '' ? null : formData.private_code
    return data
  }, [formData, touchedFields])

  const mutation = useMutation(
    (allowPartial: boolean) =>
      putBulkEditEvents({
        event_ids: eventIds,
        data: payload,
        allow_partial: allowPartial,
      }),
    {
      onSuccess: (response) => {
        handleToast(toast, response.message || `Successfully updated ${response.updated_count} event(s)`)
        setConflictData(null)
        onSuccess()
        onClose()
      },
      onError: (error: any, allowPartial) => {
        const response = error?.response?.data
        if (response?.status_code === 409 && !allowPartial) {
          setConflictData({
            eligible_ids: response.eligible_ids ?? [],
            blocked_events: response.blocked_events ?? [],
          })
          return
        }
        handleToast(toast, response?.message || 'Failed to bulk edit events')
      },
    }
  )

  const handleSubmit = () => {
    if (Object.keys(payload).length === 0) {
      handleToast(toast, 'No changes selected')
      return
    }
    mutation.mutate(false)
  }

  const getDisplayValue = (field: keyof BulkEditFormData) => {
    if (!touchedFields[field]) return 'No change'

    switch (field) {
      case 'type':
      case 'secondary_type':
        return formData[field] ? getEventLabel(formData[field]) : 'Cleared'
      case 'surface':
        return formData.surface !== null ? SURFACE_TYPES[formData.surface] : 'Cleared'
      case 'skill_level':
        return formData.skill_level.length > 0
          ? formData.skill_level.map((level) => SKILL_LEVEL_LABELS[level]).join(', ')
          : 'No levels selected'
      case 'max_players':
        return `${formData.max_players} slots`
      case 'private_code':
        return formData.private_code === '' ? 'Cleared' : formData.private_code
      default:
        return String(formData[field as keyof BulkEditFormData] ?? '')
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Bulk Edit Events</ModalHeader>
          <ModalBody pt="0">
            <Text variant="subtle" mb="16px">
              Applying changes to {eventIds.length} selected event{eventIds.length === 1 ? '' : 's'}.
            </Text>

            <BulkEditRow label="Title" value={getDisplayValue('title')} onEdit={onTitleOpen} />
            <BulkEditRow label="Description" value={getDisplayValue('description')} onEdit={onDescriptionOpen} />
            <BulkEditRow label="Event Type" value={getDisplayValue('type')} onEdit={onTypeOpen} />
            <BulkEditRow label="Secondary Type" value={getDisplayValue('secondary_type')} onEdit={onSecondaryTypeOpen} />
            <BulkEditRow label="Skill Level" value={getDisplayValue('skill_level')} onEdit={() => {
              setSelectedSkillLevels(formData.skill_level)
              onSkillLevelOpen()
            }} />
            <BulkEditRow label="Surface" value={getDisplayValue('surface')} onEdit={onSurfaceOpen} />
            <BulkEditRow label="Court" value={getDisplayValue('court')} onEdit={onCourtOpen} />
            <BulkEditRow label="Start Time" value={getDisplayValue('start_time')} onEdit={onTimeOpen} />
            <BulkEditRow label="Duration (minutes)" value={getDisplayValue('duration')} onEdit={onDurationOpen} />
            <BulkEditRow label="Fee" value={getDisplayValue('fee')} onEdit={onFeeOpen} />
            <BulkEditRow label="Venue Fixed Rate" value={getDisplayValue('venue_fixed_rate')} onEdit={onVenueFixedRateOpen} />
            <BulkEditRow label="Venue Hourly Rate" value={getDisplayValue('venue_hourly_rate')} onEdit={onVenueHourlyRateOpen} />
            <BulkEditRow label="Max Players" value={getDisplayValue('max_players')} onEdit={onMaxPlayersOpen} />
            <BulkEditRow label="Private Code" value={getDisplayValue('private_code')} onEdit={onPrivateCodeOpen} />
          </ModalBody>
          <ModalFooter gap="12px">
            <Button variant="light" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="heavy" onClick={handleSubmit} isLoading={mutation.isLoading}>
              Apply Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!conflictData} onClose={() => setConflictData(null)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Some Events Need To Be Skipped</ModalHeader>
          <ModalBody pt="0">
            <Text variant="subtle" mb="16px">
              {conflictData?.eligible_ids.length ?? 0} event{conflictData?.eligible_ids.length === 1 ? '' : 's'} can be updated. The events below will be skipped.
            </Text>
            <Flex direction="column" gap="10px">
              {conflictData?.blocked_events.map((event) => (
                <Box key={event.id} p="12px" border="1px solid #DCE2EA" borderRadius="10px">
                  <Text variant="title" fontSize="sm">
                    {event.title}
                  </Text>
                  <Text variant="subtle" fontSize="sm">
                    {event.reason}
                  </Text>
                </Box>
              ))}
            </Flex>
          </ModalBody>
          <ModalFooter gap="12px">
            <Button variant="light" onClick={() => setConflictData(null)}>
              Back
            </Button>
            <Button
              variant="heavy"
              isLoading={mutation.isLoading}
              isDisabled={!conflictData?.eligible_ids.length}
              onClick={() => mutation.mutate(true)}
            >
              Continue With Eligible Events
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <TextEditModal
        isOpen={isTitleOpen}
        title="Edit Title"
        label="Title"
        placeholder="Event title"
        onClose={onTitleClose}
        onSave={(value) => updateField('title', value)}
        initialValue={formData.title}
        isLoading={mutation.isLoading}
      />
      <TextareaEditModal
        isOpen={isDescriptionOpen}
        title="Edit Description"
        label="Description"
        onClose={onDescriptionClose}
        onSave={(value) => updateField('description', value)}
        initialValue={formData.description}
        isLoading={mutation.isLoading}
      />
      <SelectionEditModal
        isOpen={isTypeOpen}
        title="Edit Event Type"
        label="Event Type"
        choices={EVENT_TYPE_LABELS_FILTERED}
        onClose={onTypeClose}
        onSave={(value) => updateField('type', value)}
        initialValue={formData.type ? getEventLabel(formData.type) : EVENT_TYPE_LABELS_FILTERED[0]}
        isLoading={mutation.isLoading}
      />
      <SelectionEditModal
        isOpen={isSecondaryTypeOpen}
        title="Edit Secondary Type"
        label="Secondary Type"
        choices={EVENT_TYPE_LABELS_FILTERED}
        onClose={onSecondaryTypeClose}
        onSave={(value) => updateField('secondary_type', value)}
        initialValue={formData.secondary_type ? getEventLabel(formData.secondary_type) : EVENT_TYPE_LABELS_FILTERED[0]}
        isLoading={mutation.isLoading}
      />
      <SelectionEditModal
        isOpen={isSurfaceOpen}
        title="Edit Surface"
        label="Surface"
        choices={SURFACE_TYPES}
        onClose={onSurfaceClose}
        onSave={(value) => updateField('surface', parseInt(value, 10))}
        initialValue={formData.surface !== null ? SURFACE_TYPES[formData.surface] : SURFACE_TYPES[0]}
        isLoading={mutation.isLoading}
      />
      <TextEditModal
        isOpen={isCourtOpen}
        title="Edit Court"
        label="Court"
        placeholder="Court"
        onClose={onCourtClose}
        onSave={(value) => updateField('court', value)}
        initialValue={formData.court}
        isLoading={mutation.isLoading}
        allowEmpty
      />
      <TimeEditModal
        isOpen={isTimeOpen}
        title="Edit Start Time"
        label="Start Time"
        onClose={onTimeClose}
        onSave={(value) => updateField('start_time', value)}
        initialValue={formData.start_time}
        isLoading={mutation.isLoading}
      />
      <TextEditModal
        isOpen={isDurationOpen}
        title="Edit Duration"
        label="Duration in minutes"
        placeholder="60"
        onClose={onDurationClose}
        onSave={(value) => updateField('duration', value)}
        initialValue={formData.duration}
        isLoading={mutation.isLoading}
      />
      <TextEditModal
        isOpen={isFeeOpen}
        title="Edit Fee"
        label="Fee"
        placeholder="0.00"
        onClose={onFeeClose}
        onSave={(value) => updateField('fee', value)}
        initialValue={formData.fee}
        isLoading={mutation.isLoading}
      />
      <TextEditModal
        isOpen={isVenueFixedRateOpen}
        title="Edit Venue Fixed Rate"
        label="Venue Fixed Rate"
        placeholder="0.00"
        onClose={onVenueFixedRateClose}
        onSave={(value) => updateField('venue_fixed_rate', value)}
        initialValue={formData.venue_fixed_rate}
        isLoading={mutation.isLoading}
      />
      <TextEditModal
        isOpen={isVenueHourlyRateOpen}
        title="Edit Venue Hourly Rate"
        label="Venue Hourly Rate"
        placeholder="0.00"
        onClose={onVenueHourlyRateClose}
        onSave={(value) => updateField('venue_hourly_rate', value)}
        initialValue={formData.venue_hourly_rate}
        isLoading={mutation.isLoading}
      />
      <SliderEditModal
        isOpen={isMaxPlayersOpen}
        title="Edit Max Players"
        label="Max Players"
        onClose={onMaxPlayersClose}
        onSave={(value) => updateField('max_players', value)}
        initialValue={formData.max_players}
        isLoading={mutation.isLoading}
      />
      <TextEditModal
        isOpen={isPrivateCodeOpen}
        title="Edit Private Code"
        label="Private Code"
        placeholder="Leave blank to clear"
        onClose={onPrivateCodeClose}
        onSave={(value) => updateField('private_code', value)}
        initialValue={formData.private_code}
        isLoading={mutation.isLoading}
        allowEmpty
      />

      <Modal isOpen={isSkillLevelOpen} onClose={onSkillLevelClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Edit Skill Level</ModalHeader>
          <ModalBody pt="0">
            <CustomTabsMultiple
              value={selectedSkillLevels.join(',')}
              onChange={(value) => {
                const levels = value
                  .split(',')
                  .filter(Boolean)
                  .map((item) => parseInt(item, 10))
                setSelectedSkillLevels(levels)
              }}
              choices={SKILL_LEVEL_LABELS}
              results={SKILL_LEVEL_LABELS.map((_, index) => index.toString())}
            />
          </ModalBody>
          <ModalFooter gap="12px">
            <Button variant="light" onClick={onSkillLevelClose}>
              Cancel
            </Button>
            <Button
              variant="heavy"
              isLoading={mutation.isLoading}
              onClick={() => updateField('skill_level', selectedSkillLevels)}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default BulkEditEventsModal
